import React, { useState, useEffect, useRef } from 'react';
import '../styles/PhotoCapture.css';

const PhotoCapture = ({ gridCount, layout, photos, onCapture, onVideoCapture, onRetake, timeLeft, onComplete, indukName }) => {
  const [countdown, setCountdown] = useState(5);
  const [preparationCountdown, setPreparationCountdown] = useState(4);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isRetaking, setIsRetaking] = useState(false);
  const [retakeIndex, setRetakeIndex] = useState(null);
  const [shouldAutoCapture, setShouldAutoCapture] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const [recordedVideos, setRecordedVideos] = useState(Array(gridCount).fill(null));

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const startCamera = async () => {
      try {
        // First, try to get available video devices
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        
        if (videoDevices.length === 0) {
          throw new Error('No camera devices found. Please connect a camera and try again.');
        }

        // Try to get the camera stream with specific constraints
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: 'user' // Prefer front camera if available
          }
        });
        
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error('Error accessing camera:', err);
        // Show a more user-friendly error message
        alert(`Camera Error: ${err.message || 'Unable to access camera. Please make sure your camera is connected and not being used by another application.'}`);
      }
    };

    startCamera();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Timer untuk persiapan
  useEffect(() => {
    let timer;
    if (isPreparing) {
      timer = setInterval(() => {
        setPreparationCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsPreparing(false);
            setIsCapturing(true);
            return 4;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isPreparing]);

  // Timer untuk pengambilan foto
  useEffect(() => {
    let timer;
    if (isCapturing) {
      // Mulai record video saat countdown dimulai
      if (countdown === 5) {
        startRecording();
      }
      
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            capturePhoto();
            return 5;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isCapturing, countdown]);

  // Auto capture untuk foto berikutnya
  useEffect(() => {
    let autoCaptureTimer;
    if (!isCapturing && !isPreparing && shouldAutoCapture && currentPhotoIndex < gridCount && !isRetaking) {
      // Cari slot foto kosong berikutnya
      const nextEmptyIndex = photos.findIndex((photo, index) => index > currentPhotoIndex && photo === null);
      if (nextEmptyIndex !== -1) {
        autoCaptureTimer = setTimeout(() => {
          setCurrentPhotoIndex(nextEmptyIndex);
          setIsPreparing(true);
        }, 1000);
      }
    }
    return () => clearTimeout(autoCaptureTimer);
  }, [currentPhotoIndex, isCapturing, isPreparing, gridCount, isRetaking, photos, shouldAutoCapture]);

  // Timer untuk rekaman video (5 detik) - dimulai setelah foto diambil
  useEffect(() => {
    let timer;
    if (isRecording) {
      timer = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= 5) {
            clearInterval(timer);
            stopRecording();
            return 0;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isRecording]);

  const startCapture = () => {
    // Cari slot foto kosong pertama
    const firstEmptyIndex = photos.findIndex(photo => photo === null);
    if (firstEmptyIndex !== -1) {
      setCurrentPhotoIndex(firstEmptyIndex);
      setIsPreparing(true);
      setIsRetaking(false);
      setRetakeIndex(null);
      setShouldAutoCapture(true);
    }
  };

  const startRetake = (index) => {
    setRetakeIndex(index);
    setCurrentPhotoIndex(index);
    setIsPreparing(true);
    setIsRetaking(true);
    setShouldAutoCapture(true);
  };

  const startRecording = async () => {
    if (!streamRef.current) return;
    try {
      const recorder = new MediaRecorder(streamRef.current, { mimeType: 'video/webm' });
      const chunks = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        // Convert blob to base64 and save to localStorage
        const reader = new FileReader();
        reader.onloadend = function() {
          const base64data = reader.result;
          // Save to localStorage with key per session and index
          if (indukName) {
            localStorage.setItem(`recordedVideo_${indukName}_${currentPhotoIndex}`, base64data);
          }
          // Simpan ke state
          setRecordedVideos(prev => {
            const arr = [...prev];
            arr[currentPhotoIndex] = base64data;
            return arr;
          });
          // Kirim ke parent
          if (onVideoCapture) {
            const newVideos = [...recordedVideos];
            newVideos[currentPhotoIndex] = base64data;
            onVideoCapture(newVideos);
          }
        };
        reader.readAsDataURL(blob);
      };
      recorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      setRecordingTime(0);
    } catch (err) {
      console.error('Error starting recording:', err);
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
    }
    setIsRecording(false);
    setRecordingTime(0);
  };

  const capturePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      
      // Pastikan canvas memiliki background yang tepat
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Gambar video ke canvas
      ctx.drawImage(videoRef.current, 0, 0);
      
      // Konversi ke data URL dengan kualitas tinggi
      const photo = canvas.toDataURL('image/jpeg', 1.0);
      
      if (isRetaking) {
        // Update foto pada index yang di-retake
        const newPhotos = [...photos];
        newPhotos[retakeIndex] = photo;
        onCapture(newPhotos);
        setIsCapturing(false);
        setIsRetaking(false);
        setRetakeIndex(null);
        
        // Kembali ke slot foto terakhir yang sedang diambil
        const lastIndex = photos.findIndex((photo, index) => index > retakeIndex && photo === null);
        if (lastIndex !== -1) {
          setCurrentPhotoIndex(lastIndex);
        }
      } else {
        // Pengambilan foto baru
        const newPhotos = [...photos];
        newPhotos[currentPhotoIndex] = photo;
        onCapture(newPhotos);
        setIsCapturing(false);
      }

      // Stop recording video setelah foto diambil
      stopRecording();
    }
  };

  async function recordVideoForSlot(slotIdx) {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
    const recorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
    const chunks = [];
    recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
    recorder.start();
    setTimeout(() => recorder.stop(), 5000); // Rekam 5 detik
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: 'video/webm' });
      setRecordedVideos(prev => {
        const arr = [...prev];
        arr[slotIdx] = blob;
        return arr;
      });
      stream.getTracks().forEach(track => track.stop());
    };
  }

  return (
    <div className="photo-capture">
      <div className="timer">Waktu tersisa: {formatTime(timeLeft)}</div>
      
      <div className="capture-container">
        <div className="preview-container">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="camera-preview"
          />
          {isPreparing && (
            <div className="countdown preparation">
              <div className="photo-position">
                {isRetaking ? (
                  `Ready`
                ) : (
                  `Set Take ${currentPhotoIndex + 1} of ${gridCount}`
                )}
              </div>
            </div>
          )}
          {isRecording && (
            <div className="countdown recording">
              <div className="recording-indicator">
                Recording Video... {recordingTime}/5s
              </div>
            </div>
          )}
          {isCapturing && !isRecording && (
            <div className="countdown">
              <div className="countdown-number">{countdown}</div>
            </div>
          )}
        </div>

        <div className="photos-grid">
          {Array.from({ length: gridCount }).map((_, index) => (
            <div key={index} className="photo-slot">
              {photos[index] ? (
                <div className="captured-photo">
                  <img src={photos[index]} alt={`Photo ${index + 1}`} />
                  <button 
                    onClick={() => startRetake(index)} 
                    className="retake-btn"
                    disabled={isCapturing || isPreparing}
                  >
                    Ambil Ulang
                  </button>
                </div>
              ) : (
                <div className="empty-slot">
                  <span>Slot {index + 1}</span>
                  {(isCapturing || isPreparing) && currentPhotoIndex === index && (
                    <div className="current-slot-indicator">
                      {isPreparing ? 'Persiapan' : 'Sedang Mengambil Foto'}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="capture-controls">
        {!isCapturing && !isPreparing && !isRecording && photos.some(photo => photo === null) && (
          <button 
            onClick={startCapture} 
            className="capture-btn"
          >
            Mulai Ambil Foto & Video
          </button>
        )}
        {!isCapturing && !isPreparing && photos.every(photo => photo !== null) && (
          <button 
            onClick={onComplete} 
            className="complete-btn"
          >
            Selesai
          </button>
        )}
      </div>
    </div>
  );
};

export default PhotoCapture; 